// Hämtar aktiviteter till tidplansjämförelsen från det som redan finns i projektet:
// etapper (Dok 32), arbetsberedningar och arbetsområdesutredningar (Dok 10).
import { supabase } from "@/integrations/supabase/client";
import type { PlannedActivity } from "@/components/BasProcess/ScheduleCompare";

/** Gissar typ av arbete utifrån text, så raden hamnar rätt i riskparen. */
export const guessKind = (text: string): string => {
  const t = (text || "").toLowerCase();
  const rules: Array<[RegExp, string]> = [
    [/asbest|sanering|pcb/, "Asbest/sanering"],
    [/heta arbeten|svets|skär|lödn/, "Heta arbeten"],
    [/rivning|riv /, "Rivning"],
    [/lyft|kran|telfer/, "Lyft"],
    [/el\b|elarbete|ställverk|spänning|högspänning/, "El"],
    [/kemikal|epoxi|brandfarlig/, "Kemikalier"],
    [/tak|fasad|ställning|hög höjd|fallskydd/, "Arbete på hög höjd"],
    [/schakt|transport|grävning|fordon/, "Transport/schakt"],
    [/damm|buller|bilning|slipning/, "Damm/buller"],
    [/trafik|väg|gata/, "Trafikmiljö"],
    [/natt|ensam/, "Ensamarbete/natt"],
    [/vatten|vvs|våtrum|tätskikt|stambyte/, "Vatten/våtarbete"],
    [/verksamhet|boende|elev|patient|kund|tredje man/, "Pågående verksamhet/tredje man"],
  ];
  for (const [re, kind] of rules) if (re.test(t)) return kind;
  return "Allmänt arbete";
};

interface WorkPrepFields {
  arbetsplats?: string;
  startdatum?: string;
  slutdatum?: string;
  beskrivArbetet?: string;
  gjordAv?: string;
  vilkaUtför?: string[];
}

const row = (p: Partial<PlannedActivity>): PlannedActivity => ({
  id: crypto.randomUUID(),
  name: "",
  actor: "",
  area: "",
  start: "",
  end: "",
  kind: "Allmänt arbete",
  stage: "",
  ...p,
});

export interface ScheduleImport {
  activities: PlannedActivity[];
  stages: string[];
}

/** Läser etapper, arbetsberedningar och arbetsområden och gör om dem till aktivitetsrader. */
export const importScheduleActivities = async (projectId: string): Promise<ScheduleImport> => {
  const [stagesRes, prepsRes, surveysRes] = await Promise.all([
    supabase
      .from("project_stages")
      .select("id, name, start_date, end_date, description, bas_u_name")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true }),
    supabase
      .from("work_preparations")
      .select("id, name, data, stage_id")
      .eq("project_id", projectId)
      .is("deleted_at", null),
    supabase
      .from("work_area_surveys")
      .select("id, area_name, adjacent_activity, status, stage_id, planned_start, planned_end")
      .eq("project_id", projectId),

  ]);

  const stageRows = (stagesRes.data || []) as Array<{
    id: string;
    name: string;
    start_date: string | null;
    end_date: string | null;
    description: string | null;
    bas_u_name: string | null;
  }>;
  const stageName = new Map(stageRows.map((s) => [s.id, s.name]));
  const stages = stageRows.map((s) => s.name);

  const activities: PlannedActivity[] = [];

  stageRows.forEach((s) => {
    activities.push(
      row({
        name: s.name,
        actor: s.bas_u_name || "",
        area: s.name,
        stage: s.name,
        start: s.start_date || "",
        end: s.end_date || "",
        kind: guessKind(`${s.name} ${s.description || ""}`),
        source: "etapp",
      })
    );
  });

  ((prepsRes.data || []) as Array<{
    id: string;
    name: string;
    data: unknown;
    stage_id: string | null;
  }>).forEach((p) => {
    const fields = ((p.data as { fields?: WorkPrepFields } | null)?.fields || {}) as WorkPrepFields;
    activities.push(
      row({
        name: p.name,
        actor: (fields.vilkaUtför || []).join(", ") || fields.gjordAv || "",
        area: fields.arbetsplats || "",
        stage: (p.stage_id && stageName.get(p.stage_id)) || "",
        start: fields.startdatum || "",
        end: fields.slutdatum || "",
        kind: guessKind(`${p.name} ${fields.beskrivArbetet || ""}`),
        source: "arbetsberedning",
      })
    );
  });

  ((surveysRes.data || []) as Array<{
    id: string;
    area_name: string;
    adjacent_activity: string | null;
    stage_id: string | null;
    planned_start: string | null;
    planned_end: string | null;
  }>).forEach((s) => {
    activities.push(
      row({
        name: s.adjacent_activity ? `Pågående: ${s.adjacent_activity}` : `Arbete i ${s.area_name}`,
        actor: "",
        area: s.area_name,
        stage: (s.stage_id && stageName.get(s.stage_id)) || "",
        start: s.planned_start || "",
        end: s.planned_end || "",
        kind: s.adjacent_activity
          ? "Pågående verksamhet/tredje man"
          : guessKind(s.area_name),
        source: "arbetsområdesutredning",
      })
    );
  });


  return { activities, stages };
};

/** Slår ihop importerade rader med befintliga utan att skapa dubbletter. */
export const mergeActivities = (
  existing: PlannedActivity[],
  incoming: PlannedActivity[]
): PlannedActivity[] => {
  const key = (a: PlannedActivity) =>
    `${(a.name || "").trim().toLowerCase()}|${(a.area || "").trim().toLowerCase()}`;
  const seen = new Set(existing.map(key));
  const added = incoming.filter((a) => {
    const k = key(a);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return [...existing, ...added];
};
